import type { LICStateProperties } from "@/api/model/lic-state";
import type { LicVirtualDeviceEquipmentDetails } from "@/utils/lic-state";

/**
 * A virtual device of the dashboard LIC state card: the LIC device entry joined with
 * the account element it controls (null when the element is unknown).
 */
type DashboardVirtualDevice = {
  equipment: LicVirtualDeviceEquipmentDetails | null;
  virtualDevice: LICStateProperties["devices"][number];
};

/**
 * Returns the display name of an account element (sectors and tanks use `name`, pumps
 * and reservoirs use `label`).
 *
 * @param equipment - Resolved equipment of a virtual device.
 * @returns The element display name.
 */
function getEquipmentName(equipment: LicVirtualDeviceEquipmentDetails): string {
  return "name" in equipment.element ? equipment.element.name : equipment.element.label;
}

/**
 * Returns the label of a tank fertigation device: singular when exactly one tank of the
 * project is active (no `end_date`), plural otherwise.
 *
 * @param equipment - Resolved tank fertigation equipment (the project element).
 * @returns "Tanque" or "Tanques".
 */
function getTankFertigationLabel(equipment: Extract<LicVirtualDeviceEquipmentDetails, { elementType: "tank_ferti" }>) {
  const activeTankCount = equipment.element.project_tanks.filter((projectTank) => !projectTank.end_date).length;
  return activeTankCount === 1 ? "Tanque" : "Tanques";
}

/**
 * Builds the label shown by the dashboard LIC state card for a virtual device, e.g.
 * "Reservatório Main", "Bomba de Água Irrigação (Retro)", "Setor A" or "Tanques".
 *
 * @param device - Virtual device with its resolved equipment (null when unknown).
 * @returns The label, empty string when the equipment is unknown and the device is not
 *   a tank fertigation device.
 */
export function getDashboardVirtualDeviceLabel(device: DashboardVirtualDevice): string {
  if (!device.equipment) {
    return device.virtualDevice.elementVariation === "tank-ferti" ? "Tanques" : "";
  }

  const { element, elementType } = device.equipment;

  switch (elementType) {
    case "reservoir":
      return `Reservatório ${getEquipmentName(device.equipment)}`;
    case "water_pump": {
      const backwashSuffix = device.virtualDevice.elementVariation === "backwash" ? " (Retro)" : "";
      return `Bomba de Água ${getEquipmentName(device.equipment)}${backwashSuffix}`;
    }
    case "sector":
      return element.name.toLowerCase().includes("setor") ? element.name : `Setor ${element.name}`;
    case "tank_ferti":
      return getTankFertigationLabel(device.equipment);
  }
}
